<script lang="ts">
export interface IconPickerTabEmojiProps {
	readonly?: boolean;
	disabled?: boolean;
	selectedShortcode?: string;
}

type EmojiRow =
	| { type: 'header'; id: string; key: string }
	| { type: 'icons'; id: string; codes: readonly string[] };

interface EmojiGridGeometry {
	rowIndexByShortcode: Map<string, number>;
	flatShortcodes: string[];
	rowLengths: number[];
}
</script>

<script lang="ts" setup>
import type { AcceptableValue } from 'reka-ui';
import type { Tone } from '#layers/ui/utils/emoji-glyph';
import { createReusableTemplate, refDebounced } from '@vueuse/core';
import { chunk } from 'lodash-es';
import { PopoverAnchor, PopoverContent, PopoverPortal, PopoverRoot, RadioGroupItem, RadioGroupRoot } from 'reka-ui';
import { computed, nextTick, ref, unref, useId, useTemplateRef, watch } from 'vue';
import { RecycleScroller } from 'vue-virtual-scroller';
import { useEmojiSkinTone } from '#layers/ui/composables/use-emoji-skin-tone';
import { useGridNavigation } from '#layers/ui/composables/use-grid-navigation';
import { useLongPress } from '#layers/ui/composables/use-long-press';
import { EMOJI_GROUPS } from '#layers/ui/utils/emoji-data';
import { getTonedGlyph, isToneableShortcode, SKIN_TONE_VALUES } from '#layers/ui/utils/emoji-glyph';
import { SEARCHABLE_EMOJI_SHORTCODES } from '#layers/ui/utils/emoji-search';
import IconPickerEmptyState from './IconPickerEmptyState.vue';

const props = defineProps<IconPickerTabEmojiProps>();

const emit = defineEmits<{
	select: [shortcode: string];
}>();

const [DefineEmojiCell, ReuseEmojiCell] = createReusableTemplate<{ shortcode: string }>();

const COLUMNS = 12;
const ROW_SIZE = 32;
const POPOVER_OFFSET = 4;
const { tone, applyTone } = useEmojiSkinTone();

const search = ref('');

const tonePopoverShortcode = ref<string | null>(null);
const tonePopoverAnchor = ref<HTMLElement | null>(null);
const tonePopoverPanel = useTemplateRef<HTMLElement>('tonePopoverPanel');
let suppressNextClick = false;

const gridRef = useTemplateRef<HTMLElement>('grid');
const scrollerRef = useTemplateRef<{ scrollToItem: (index: number) => void }>('scroller');
const searchInputRef = useTemplateRef('searchInput');

const listboxId = useId();

function emojiCellId(shortcode: string): string {
	return `${listboxId}-${shortcode}`;
}

const focusedCellId = computed(() => {
	const shortcode = focusedShortcode.value;
	return shortcode === null ? undefined : emojiCellId(shortcode);
});
// Filtering scans roughly 2k shortcodes per keystroke.
const debouncedSearch = refDebounced(search, 150);
const trimmedSearch = computed(() => debouncedSearch.value.trim().toLowerCase());
const isSearching = computed(() => trimmedSearch.value.length > 0);

const filteredShortcodes = computed(() => {
	const q = trimmedSearch.value;
	if (!q) return [];

	const matches: string[] = [];

	for (const entry of SEARCHABLE_EMOJI_SHORTCODES) {
		if (entry.normalized.includes(q)) matches.push(entry.shortcode);
	}

	return matches;
});

function toIconRows(shortcodes: readonly string[], idPrefix: string): EmojiRow[] {
	return chunk(shortcodes, COLUMNS)
		.map((codes, index) => ({ type: 'icons', id: `${idPrefix}-${index}`, codes }));
}

const filteredRows = computed<EmojiRow[]>(() => toIconRows(filteredShortcodes.value, 's'));

const groupedRows = computed<EmojiRow[]>(() => {
	const out: EmojiRow[] = [];

	for (const group of EMOJI_GROUPS) {
		out.push(
			{ type: 'header', id: `h-${group.key}`, key: group.key },
			...toIconRows(group.shortcodes, `g-${group.key}`),
		);
	}

	return out;
});

const rows = computed<EmojiRow[]>(() => isSearching.value ? filteredRows.value : groupedRows.value);

const gridGeometry = computed<EmojiGridGeometry>(() => {
	const rowIndexByShortcode = new Map<string, number>();
	const flatShortcodes: string[] = [];
	const rowLengths: number[] = [];

	for (const [idx, row] of rows.value.entries()) {
		if (row.type !== 'icons') continue;

		rowLengths.push(row.codes.length);

		for (const code of row.codes) {
			rowIndexByShortcode.set(code, idx);
			flatShortcodes.push(code);
		}
	}

	return { rowIndexByShortcode, flatShortcodes, rowLengths };
});

const flatShortcodes = computed(() => gridGeometry.value.flatShortcodes);
const rowLengths = computed(() => gridGeometry.value.rowLengths);

const {
	focusedIndex,
	setFocused,
	handleArrowDown,
	handleArrowLeft,
	handleArrowRight,
	handleArrowUp,
	handleActivate,
} = useGridNavigation({
	rows: rowLengths,
	disabled: () => Boolean(props.readonly || props.disabled),
	wrapHorizontal: true,
	onActivate: () => {
		const shortcode = flatShortcodes.value[focusedIndex.value];
		if (shortcode) handleSelect(shortcode);
	},
});

const focusedShortcode = computed<string | null>(() => flatShortcodes.value[focusedIndex.value] ?? null);
const selectedToneValue = computed(() => tone.value ?? 0);

// Search results and rows can invalidate the focused index.
watch([() => search.value, isSearching], () => {
	setFocused(-1);
});

function groupLabel(key: string): string {
	return `icon_picker.emojis.groups.${key.replaceAll('-', '_')}`;
}

function handleSelect(shortcode: string) {
	if (suppressNextClick) {
		suppressNextClick = false;
		return;
	}

	if (props.readonly || props.disabled) return;
	emit('select', applyTone(shortcode));
}

function focusGridForShortcode(shortcode: string | null) {
	if (shortcode === null) return;
	const index = flatShortcodes.value.indexOf(shortcode);
	if (index !== -1) setFocused(index);

	nextTick(() => {
		gridRef.value?.focus();
	});
}

function closeTonePopover({ focusGrid = false } = {}) {
	const shortcode = tonePopoverShortcode.value;
	tonePopoverShortcode.value = null;
	tonePopoverAnchor.value = null;
	suppressNextClick = false;
	if (focusGrid) focusGridForShortcode(shortcode);
}

function openTonePopover(shortcode: string, anchor: HTMLElement) {
	tonePopoverShortcode.value = shortcode;
	tonePopoverAnchor.value = anchor;
}

function handleTonePopoverOpenUpdate(open: boolean) {
	if (!open) closeTonePopover();
}

function chooseTone(value: AcceptableValue) {
	if (props.readonly || props.disabled) {
		closeTonePopover();
		return;
	}

	const selectedTone = Number(value) as Exclude<Tone, null>;
	const shortcode = tonePopoverShortcode.value;
	tone.value = selectedTone === 0 ? null : selectedTone;

	if (shortcode !== null) {
		emit('select', applyTone(shortcode));
	}

	closeTonePopover();
}

function handleTonePopoverEscape() {
	closeTonePopover({ focusGrid: true });
}

let pendingShortcode: string | null = null;
let pendingAnchor: HTMLElement | null = null;

const sharedLongPress = useLongPress(() => {
	if (pendingShortcode === null || pendingAnchor === null) return;
	suppressNextClick = true;
	openTonePopover(pendingShortcode, pendingAnchor);
});

function onCellPointerdown(shortcode: string, event: PointerEvent) {
	if (props.readonly || props.disabled) return;
	if (!isToneableShortcode(shortcode)) return;
	const target = event.currentTarget;
	if (!(target instanceof HTMLElement)) return;
	pendingShortcode = shortcode;
	pendingAnchor = target;
	sharedLongPress.onPointerdown(event);
}

function onCellPointerEnd() {
	pendingShortcode = null;
	pendingAnchor = null;
	sharedLongPress.onPointerup();
}

function onCellContextmenu(shortcode: string, event: MouseEvent) {
	if (props.readonly || props.disabled) return;
	if (!isToneableShortcode(shortcode)) return;
	const target = event.currentTarget;
	if (!(target instanceof HTMLElement)) return;
	event.preventDefault();

	// iOS Safari fires contextmenu after a touch long-press.
	if (suppressNextClick) {
		suppressNextClick = false;
		return;
	}

	openTonePopover(shortcode, target);
}

const isTonePopoverOpen = computed(() => tonePopoverShortcode.value !== null);

defineExpose({
	focusSearchInput,
	isTonePopoverOpen,
	tonePopoverPanel,
});

async function focusSearchInput() {
	setFocused(-1);
	await nextTick();
	unref(searchInputRef.value?.inputRef)?.focus();
}

const isOnFirstRow = computed(() => {
	const firstRowLength = rowLengths.value[0] ?? 0;
	return focusedIndex.value >= 0 && focusedIndex.value < firstRowLength;
});

watch(focusedIndex, (index) => {
	if (index < 0) return;
	const shortcode = flatShortcodes.value[index];
	if (shortcode === undefined) return;
	const renderRow = gridGeometry.value.rowIndexByShortcode.get(shortcode);
	if (renderRow === undefined) return;

	const scroller = scrollerRef.value;
	if (scroller && typeof scroller.scrollToItem === 'function') scroller.scrollToItem(renderRow);
});

function handleSearchKeydown(event: KeyboardEvent) {
	if (event.key !== 'ArrowDown') return;
	if (flatShortcodes.value.length === 0) return;
	event.preventDefault();
	setFocused(0);
	gridRef.value?.focus();
}

function handleGridKeydown(event: KeyboardEvent) {
	if (flatShortcodes.value.length === 0) return;

	switch (event.key) {
		case 'ArrowRight':
			event.preventDefault();
			handleArrowRight(event);
			return;
		case 'ArrowLeft':
			event.preventDefault();
			handleArrowLeft(event);
			return;
		case 'ArrowDown':
			event.preventDefault();
			handleArrowDown(event);
			return;
		case 'ArrowUp':
			event.preventDefault();

			if (isOnFirstRow.value) {
				focusSearchInput();
				return;
			}

			handleArrowUp(event);
			return;
		case 'Enter':
		case ' ':
			event.preventDefault();
			handleActivate(event);
			return;
		case 'Escape':
			event.preventDefault();
			focusSearchInput();
			return;
		default:
			if (event.key.length === 1 && !event.ctrlKey && !event.metaKey) {
				focusSearchInput();
			}
	}
}
</script>

<template>
	<div class="relative flex min-h-0 flex-1 flex-col">
		<DInput
			ref="searchInput"
			v-model="search"
			:placeholder="$t('icon_picker.emoji_search_placeholder')"
			variant="none"
			class="w-full border-b border-default"
			size="md"
			:ui="{ base: 'h-11 px-4' }"
			:readonly
			:disabled
			@keydown="handleSearchKeydown"
		/>

		<div class="relative flex min-h-0 flex-1 flex-col overflow-hidden">
			<IconPickerEmptyState v-if="isSearching && filteredRows.length === 0">
				{{ $t('icon_picker.emoji_no_results') }}
			</IconPickerEmptyState>

			<div
				v-else
				ref="grid"
				role="listbox"
				tabindex="-1"
				:aria-activedescendant="focusedCellId"
				:aria-label="$t('icon_picker.tab_emoji')"
				class="flex min-h-0 flex-1 flex-col overflow-hidden outline-none"
				@keydown="handleGridKeydown"
			>
				<RecycleScroller
					ref="scroller"
					:items="rows"
					:item-size="ROW_SIZE"
					key-field="id"
					direction="vertical"
					class="
						flex-1 scrollbar-thin [scrollbar-color:var(--ui-border-highlighted)_transparent] overflow-x-hidden overflow-y-auto
						overscroll-none px-2 py-1
					"
				>
					<template #default="{ item: row }">
						<div
							v-if="row.type === 'header'"
							data-testid="emoji-group-header"
							class="flex h-8 items-end px-1 pb-1 text-xs font-medium tracking-wide text-dimmed uppercase"
						>
							{{ $t(groupLabel(row.key)) }}
						</div>
						<div v-else class="grid grid-cols-12">
							<DefineEmojiCell v-slot="{ shortcode }">
								<div
									:id="emojiCellId(shortcode)"
									role="option"
									tabindex="-1"
									:data-testid="`emoji-cell-${shortcode}`"
									:aria-selected="props.selectedShortcode === shortcode || undefined"
									class="
										inline-flex h-8 w-full cursor-pointer items-center justify-center rounded-sm p-1 text-lg
										hover:bg-accented
										aria-selected:bg-primary/10
									"
									:class="{ 'bg-accented': focusedShortcode === shortcode }"
									:aria-haspopup="isToneableShortcode(shortcode) ? 'dialog' : undefined"
									:aria-expanded="isToneableShortcode(shortcode) ? (tonePopoverShortcode === shortcode) : undefined"
									@pointerdown="onCellPointerdown(shortcode, $event)"
									@pointerup="onCellPointerEnd"
									@pointerleave="onCellPointerEnd"
									@pointermove="sharedLongPress.onPointermove($event)"
									@pointercancel="onCellPointerEnd"
									@contextmenu="onCellContextmenu(shortcode, $event)"
									@click="handleSelect(shortcode)"
								>
									{{ getTonedGlyph(shortcode, tone) }}
								</div>
							</DefineEmojiCell>
							<template v-for="shortcode in row.codes" :key="shortcode">
								<DTooltip
									v-if="isToneableShortcode(shortcode)"
									:text="$t('icon_picker.hold_to_change_skin_tone')"
								>
									<ReuseEmojiCell :shortcode />
								</DTooltip>
								<ReuseEmojiCell v-else :shortcode />
							</template>
						</div>
					</template>
				</RecycleScroller>
			</div>
		</div>

		<PopoverRoot
			:open="tonePopoverShortcode !== null"
			modal
			@update:open="handleTonePopoverOpenUpdate"
		>
			<PopoverAnchor v-if="tonePopoverAnchor" :reference="tonePopoverAnchor" />
			<PopoverPortal>
				<PopoverContent
					v-if="tonePopoverShortcode !== null"
					align="start"
					:side-offset="POPOVER_OFFSET"
					@escape-key-down="handleTonePopoverEscape"
				>
					<div
						ref="tonePopoverPanel"
						data-testid="skin-tone-popover"
						role="dialog"
						:aria-label="$t('icon_picker.choose_skin_tone')"
						class="rounded-md border border-default bg-default p-1 shadow-lg"
					>
						<RadioGroupRoot
							:model-value="selectedToneValue"
							orientation="horizontal"
							:aria-label="$t('icon_picker.choose_skin_tone')"
							class="flex items-center gap-1"
							@update:model-value="chooseTone"
						>
							<RadioGroupItem
								v-for="value in SKIN_TONE_VALUES"
								:key="value"
								:value
								:data-testid="`skin-tone-option-${value}`"
								:aria-label="value === 0 ? $t('icon_picker.default_skin_tone') : $t('icon_picker.skin_tone_n', { n: value })"
								class="inline-flex size-8 cursor-pointer items-center justify-center rounded-sm p-1 text-lg hover:bg-accented"
							>
								{{ getTonedGlyph(tonePopoverShortcode, value) }}
							</RadioGroupItem>
						</RadioGroupRoot>
					</div>
				</PopoverContent>
			</PopoverPortal>
		</PopoverRoot>
	</div>
</template>
